// Native-fetch HTTP client replacing axios (ticket 25822). It reproduces only
// the slice of the axios API this codebase used, so call sites change by name
// alone (`axios.` → `api.`):
//
//   - get(url, { headers, params })       params → query string
//   - post|put|patch(url, body, { headers })  body → JSON, Content-Type set
//   - delete(url, { data, headers })      data → JSON body (axios's shape)
//
// Each call resolves to { data, status, ok } where `data` is the parsed JSON
// (an empty body → null), and REJECTS on a non-2xx response with an Error whose
// `.response` is { status, data } — matching axios so existing `.catch` and
// `response.status` paths keep working.

function buildUrl(url, params) {
  if (!params) return url
  const query = new URLSearchParams()
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null) return
    if (Array.isArray(value)) {
      value.forEach((v) => query.append(key, v))
    } else {
      query.append(key, value)
    }
  })
  const qs = query.toString()
  return qs ? `${url}?${qs}` : url
}

async function request(method, url, { headers = {}, params, body } = {}) {
  const hasBody = body !== undefined
  const init = {
    method,
    headers: hasBody ? { "Content-Type": "application/json", ...headers } : { ...headers },
  }
  if (hasBody) init.body = JSON.stringify(body)

  const response = await fetch(buildUrl(url, params), init)

  let data = null
  const text = await response.text()
  if (text) {
    try {
      data = JSON.parse(text)
    } catch {
      data = text
    }
  }

  if (!response.ok) {
    const error = new Error(`Request failed with status ${response.status}`)
    error.response = { status: response.status, data }
    throw error
  }

  return { data, status: response.status, ok: response.ok }
}

const api = {
  get: (url, { headers, params } = {}) => request("GET", url, { headers, params }),
  post: (url, body, { headers } = {}) => request("POST", url, { headers, body }),
  put: (url, body, { headers } = {}) => request("PUT", url, { headers, body }),
  patch: (url, body, { headers } = {}) => request("PATCH", url, { headers, body }),
  delete: (url, { data, headers } = {}) => request("DELETE", url, { headers, body: data }),
}

export default api
